/**
 * MixpanelServerEditor - Configuration editor for Mixpanel Server-Side
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  TextField,
  Button,
  Link,
  Alert,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  type SelectChangeEvent,
} from '@mui/material';
import type { MixpanelServerConfig, PlatformConfig } from '../../types/analytics.types';

interface MixpanelServerEditorProps {
  config: PlatformConfig;
  onChange: (config: PlatformConfig, name?: string) => void;
  disabled?: boolean;
  readOnly?: boolean;
  showSaveButton?: boolean;
  onSave?: (config: PlatformConfig, name?: string) => void;
  name?: string;
}

export const MixpanelServerEditor: React.FC<MixpanelServerEditorProps> = ({
  config,
  onChange,
  disabled = false,
  readOnly = false,
  showSaveButton = false,
  onSave,
  name,
}) => {
  const mpConfig = config as MixpanelServerConfig;
  const [localConfig, setLocalConfig] = useState<MixpanelServerConfig>(mpConfig);
  const [localName, setLocalName] = useState(name || '');

  useEffect(() => {
    setLocalConfig(mpConfig);
    setLocalName(name || '');
  }, [mpConfig, name]);

  const handleChange = useCallback(
    (field: keyof MixpanelServerConfig, value: unknown) => {
      const newConfig = { ...localConfig, [field]: value };
      setLocalConfig(newConfig);
      if (!showSaveButton) {
        onChange(newConfig, localName);
      }
    },
    [localConfig, localName, onChange, showSaveButton]
  );

  const handleRegionChange = useCallback(
    (event: SelectChangeEvent<'US' | 'EU' | 'IN'>) => {
      handleChange('region', event.target.value as 'US' | 'EU' | 'IN');
    },
    [handleChange]
  );

  const handleSave = useCallback(() => {
    if (onSave) {
      onSave(localConfig, localName);
    }
  }, [onSave, localConfig, localName]);

  const canSave =
    !!localConfig.projectToken &&
    !!localConfig.projectId &&
    !!localConfig.serviceAccountUsername &&
    !!localConfig.serviceAccountSecret;

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Mixpanel Server-Side sends purchase events directly from the server for accurate revenue tracking.
        This supplements the client-side Mixpanel tracking. The Import API requires a Service Account
        and the numeric Project ID — Project Token alone is not accepted by Mixpanel.
      </Alert>

      <TextField
        label="Project Token"
        value={localConfig.projectToken || ''}
        onChange={(e) => handleChange('projectToken', e.target.value)}
        disabled={disabled || readOnly}
        fullWidth
        required
        sx={{ mb: 2 }}
        placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
        helperText={
          <>
            Same Project Token as your client-side Mixpanel. Find it in{' '}
            <Link href="https://mixpanel.com/" target="_blank" rel="noopener">
              Mixpanel Settings → Project Settings
            </Link>
          </>
        }
      />

      <TextField
        label="Project ID"
        value={localConfig.projectId || ''}
        onChange={(e) => handleChange('projectId', e.target.value)}
        disabled={disabled || readOnly}
        fullWidth
        required
        sx={{ mb: 2 }}
        placeholder="1234567"
        helperText={
          <>
            Numeric Project ID. In Mixpanel: gear icon (bottom-left) → Settings →{' '}
            <Link
              href="https://mixpanel.com/settings/project"
              target="_blank"
              rel="noopener"
            >
              Project Settings
            </Link>{' '}
            → <b>Overview</b> → "Project Details" → Project ID. Also visible in the Access URL
            (e.g. <code>https://eu.mixpanel.com/project/<b>1234567</b></code>). If "Data
            Residency" on that page shows EU, set Server Zone below to EU.
          </>
        }
      />

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Server Zone</InputLabel>
        <Select
          value={localConfig.region || 'US'}
          onChange={handleRegionChange}
          disabled={disabled || readOnly}
          label="Server Zone"
        >
          <MenuItem value="US">US (api.mixpanel.com)</MenuItem>
          <MenuItem value="EU">EU (api-eu.mixpanel.com)</MenuItem>
          <MenuItem value="IN">IN (api-in.mixpanel.com)</MenuItem>
        </Select>
      </FormControl>

      <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
        Service Account (Required — Import API mandates Basic Auth)
      </Typography>

      <TextField
        label="Service Account Username"
        value={localConfig.serviceAccountUsername || ''}
        onChange={(e) => handleChange('serviceAccountUsername', e.target.value)}
        disabled={disabled || readOnly}
        fullWidth
        required
        sx={{ mb: 2 }}
        placeholder="your-service-account.xxxx.mp-service-account"
        helperText="Create in Mixpanel Settings → Organization Settings → Service Accounts"
      />

      <TextField
        label="Service Account Secret"
        value={localConfig.serviceAccountSecret || ''}
        onChange={(e) => handleChange('serviceAccountSecret', e.target.value)}
        disabled={disabled || readOnly}
        fullWidth
        required
        type="password"
        sx={{ mb: 2 }}
        placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
        helperText="Secret shown once when the service account is created"
      />

      {showSaveButton && (
        <Box sx={{ mt: 3 }}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={disabled || !canSave}
          >
            Save
          </Button>
        </Box>
      )}
    </Box>
  );
};
